/**
 * The six named Eras, read from `content/eras.json` and not typed into a screen.
 *
 * `game/src/screens/Ledger.tsx` used to carry the Era table as literals, and it stated two
 * different mappings fifty five lines apart: a badge wall with Era 1 as The Iron Pact and Era 2
 * as The Docks War, matching GAME-DESIGN 10.1, and an Era Record table below it with those two
 * transposed plus an "Era 0, Founding Era" that canon does not have. Every asset id in both was
 * valid and resolved, which is exactly why nothing caught it: the gate proves an id resolves and
 * it cannot prove the number written next to the name is the one canon says belongs there.
 *
 * So the pairing lives in `content/`, where the gate already validates it, and this file is the
 * one door into it. `tools/content/validate.mjs` now also asserts the six `ledger.eras.*` banners
 * are used exactly once between them, so a missing Era or a doubled banner is a failed build.
 *
 * There is no founding Era. 10.1 names six, numbered 1 to 6. `icons.legacy.founding_era` is a
 * legacy BADGE for an account created before Era 1 closed, which is a different thing wearing a
 * similar name.
 */

import eras from '../../../content/eras.json'
import type { KnownAssetId } from '../art'

export interface Era {
  n: number
  name: string
  bannerId: KnownAssetId
  /** "Every Era needs a headline rule variant, announced two weeks ahead." GAME-DESIGN 10.1. */
  headline: string
}

/**
 * The cast is needed and it is narrow. JSON import widens every string to `string`, so `bannerId`
 * arrives unbranded; what keeps it honest is the content gate, which checks each id against
 * `art/manifest.json` on every build and would refuse one that does not resolve. The cast asserts
 * a fact the build has already proved rather than one this file is hoping for.
 */
export const ERAS: readonly Era[] = eras as readonly Era[]

export const eraByNumber = (n: number): Era | null => ERAS.find((e) => e.n === n) ?? null

/** The first Era, and the one a new account is playing in. DELTAS 1f.5. */
export const FIRST_ERA = ERAS[0]
